/**
 * orchardFrostVaultIntegration - Maps Vultisig vault shares to Orchard FROST
 * key bundles for threshold-signed Orchard shielded transactions.
 *
 * Key storage: OrchardKeyBundles are stored in localStorage keyed by
 * vault UID + "orchard-frost-bundle".
 *
 * DKG model: Trusted dealer (keygen_with_dealer) generates all shares.
 *   - Share 1 stored locally (browser)
 *   - Share 2 transmitted to mobile co-signer via relay
 *
 * Signing model: Two-round Schnorr FROST over relay:
 *   Round 1: Both signers commit nonces, exchange commitments
 *   Round 2: Both signers produce signature shares, aggregator combines
 */

import {
	initOrchardFrostWasm,
	isOrchardFrostReady,
	isProperDkgAvailable,
	orchardKeygen,
	orchardKeygenWithCompatibleExtras,
	orchardCommitNonces,
	orchardSignWithNonces,
	orchardGenerateRandomizer,
	orchardAggregate,
	orchardGenerateExtras,
	hasFroztOrchardKeys,
	deriveOrchardAddressFromBundle,
	generateOrchardExtras,
	CURRENT_ORCHARD_BUNDLE_VERSION,
	type OrchardKeyBundle,
	type OrchardKeyShare,
	type OrchardPublicKeyPackage,
} from './orchardFrostBridge';
import {
	runInitiatorDkgCeremony,
	runJoinerDkgCeremony,
	runInitiatorTrustedDealerCeremony,
	runJoinerTrustedDealerCeremony,
	type DkgCeremonyCallbacks,
	type DkgCeremonyResult,
	type DkgSessionParams,
	type TrustedDealerResult,
} from '../../../../services/orchardDkgCeremony';
import type { CosignTransport } from '../../../../services/cosignTransports';

import { type CeremonyOptions } from './froztCeremonyRunner';

/** Legacy localStorage prefixes — only used to sweep stale entries
 *  from browsers upgraded across the May 2026 privacy fix. Orchard
 *  FROST shares are now in-memory only (see `_orchardBundles`
 *  below); the user persists them exclusively via `.wult` files. */
const LEGACY_STORAGE_PREFIX = 'orchard-frost-bundle:';
const LEGACY_KEEP_CHOICE_PREFIX = 'wb32:orchard-frost:keep:';
const TIP_CACHE_KEY = 'wb32:orchard-frost:last-known-tip';

/** In-memory store for the active session only. The earlier build
 *  pushed every bundle into `localStorage` keyed by vault UID, which
 *  produced a privacy leak: plain `.vult` files re-loaded later
 *  appeared to "already have Orchard" because the cache from a
 *  previous DKG / setup session was still on disk. */
const _orchardBundles = new Map<string, OrchardKeyBundle>();
/** Session-scoped "user declined the Orchard FROST setup" flags.
 *  Not persisted — decisions don't survive a reload. */
const _orchardKeepAsIs = new Set<string>();

/**
 * Sweep legacy localStorage entries left over from before the May
 * 2026 privacy fix. Wallet key material must never live in
 * localStorage. Tries to run only once per tab (cheap), but the
 * actual sweep is always safe to repeat. `__resetForTests` clears
 * the guard so test suites can exercise the sweep deterministically.
 */
let _legacySweepDone = false;
export function __resetOrchardSweepGuardForTests(): void {
	_legacySweepDone = false;
}
export function sweepLegacyOrchardLocalStorage(): void {
	if (_legacySweepDone) return;
	_legacySweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		const doomed: string[] = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key) continue;
			if (key.startsWith(LEGACY_STORAGE_PREFIX)
				|| key.startsWith(LEGACY_KEEP_CHOICE_PREFIX)) {
				doomed.push(key);
			}
		}
		for (const key of doomed) {
			try { localStorage.removeItem(key); } catch { /* */ }
		}
		if (doomed.length > 0) {
			console.info('[orchard-frost] swept %d legacy localStorage entries',
				doomed.length);
		}
	} catch (err) {
		console.warn('[orchard-frost] legacy localStorage sweep failed:',
			(err as Error).message);
	}
}

/**
 * Conservative fallback when no chain tip has ever been observed in this
 * browser. Mainnet NU6 height — Orchard notes created before this either
 * predate consensus rules we care about or will be picked up by the
 * standard scan path. Better than `0` (which forces a genesis-wide rescan
 * every time the wallet is opened).
 */
const ORCHARD_FROST_BIRTHDAY_FALLBACK = 2726400;

/**
 * Cache the latest observed Zcash chain tip so the next bundle creation
 * (DKG / trusted-dealer) can stamp a sensible birthday height even when
 * the call site can't await an RPC round-trip. Also lets us bump the
 * birthday on existing bundles as the wallet learns the tip.
 */
export function rememberLatestZcashTip(height: number): void {
	if (!Number.isFinite(height) || height <= 0) return;
	if (typeof localStorage === 'undefined') return;
	try {
		const existing = Number(localStorage.getItem(TIP_CACHE_KEY) || 0);
		if (height > existing) {
			localStorage.setItem(TIP_CACHE_KEY, String(Math.floor(height)));
		}
	} catch { /* ignore quota / access */ }
}

/**
 * Best-known birthday height to stamp on a fresh Orchard FROST bundle.
 * A new share can never sign notes that pre-date the ceremony, so we
 * prefer the most recent chain tip we've seen; otherwise we fall back
 * to the NU6 mainnet height to keep scans tractable.
 */
export function getSmartOrchardBirthdayHeight(): number {
	if (typeof localStorage !== 'undefined') {
		try {
			const cached = Number(localStorage.getItem(TIP_CACHE_KEY) || 0);
			if (Number.isFinite(cached) && cached >= ORCHARD_FROST_BIRTHDAY_FALLBACK) {
				return Math.floor(cached);
			}
		} catch { /* ignore */ }
	}
	return ORCHARD_FROST_BIRTHDAY_FALLBACK;
}

/**
 * Persist `height` onto an existing Orchard bundle iff it improves the
 * stored value (never decrease — that would force a wider rescan than
 * the user has already proven to be safe). Returns the value actually
 * stored.
 */
export function setOrchardBundleBirthday(vaultUid: string, height: number): number {
	if (!vaultUid) return 0;
	if (!Number.isFinite(height) || height <= 0) return 0;
	const bundle = loadOrchardBundle(vaultUid);
	if (!bundle) return 0;
	const next = Math.max(Math.floor(height), Math.floor(bundle.birthday || 0));
	if (next === bundle.birthday) return next;
	bundle.birthday = next;
	storeOrchardBundle(vaultUid, bundle);
	return next;
}

// -- Feature flag --------------------------------------------

/**
 * Threshold (t-of-n) mode for Orchard FROST. When enabled, `runOrchardDkg`
 * uses the vault's real signer count/threshold (via `otherPartyIds` +
 * `minSigners`) and stashes undelivered shares in `bundle.pendingShares`.
 * Each cosigner then receives *its specific* share (looked up by its
 * Vultisig `localPartyId`) the first time it requests an Orchard DKG.
 *
 * When disabled (default for initial release) `runOrchardDkg` keeps the
 * legacy 2-of-2 behaviour: generate two shares, keep one, hand the other
 * to whoever happens to pair first. Any vault of size > 2 can still sign
 * but only ever with exactly the two devices that co-signed the bootstrap.
 *
 * Flag is read at keygen time only. Flipping it at runtime does not
 * retroactively re-key existing bundles — the user must `removeOrchardBundle`
 * and re-derive.
 */
export const ORCHARD_FROST_THRESHOLD_FLAG_KEY = 'winbit32_orchard_frost_threshold';

export function isOrchardFrostThresholdEnabled(): boolean {
	if (typeof localStorage === 'undefined') return false;
	return localStorage.getItem(ORCHARD_FROST_THRESHOLD_FLAG_KEY) === 'true';
}

export function setOrchardFrostThresholdEnabled(on: boolean): void {
	if (typeof localStorage === 'undefined') return;
	if (on) {
		localStorage.setItem(ORCHARD_FROST_THRESHOLD_FLAG_KEY, 'true');
	} else {
		localStorage.removeItem(ORCHARD_FROST_THRESHOLD_FLAG_KEY);
	}
}

/**
 * "Proper DKG" mode: run the N-party Pedersen DKG (no trusted dealer)
 * via `runInitiatorDkgCeremony` / `runJoinerDkgCeremony` instead of the
 * trusted-dealer keygen. When enabled, Orchard FROST key generation
 * requires **all N vault devices to be online once** to complete the
 * ceremony, but afterwards any T of N can co-sign without the dealer
 * ever having learned other parties' shares.
 *
 * Default: ON when `isProperDkgAvailable()` reports the WASM bundle
 * exposes `DkgParticipant`. Users on older WASM bundles (or who
 * explicitly opt out via this flag) fall back to the trusted-dealer
 * behaviour controlled by `ORCHARD_FROST_THRESHOLD_FLAG_KEY`.
 */
export const ORCHARD_FROST_PROPER_DKG_FLAG_KEY = 'winbit32_orchard_frost_proper_dkg';

export function isOrchardFrostProperDkgEnabled(): boolean {
	if (typeof localStorage === 'undefined') return false;
	const raw = localStorage.getItem(ORCHARD_FROST_PROPER_DKG_FLAG_KEY);
	if (raw === 'false') return false;
	// Default to on whenever the WASM bundle advertises proper-DKG support.
	return isProperDkgAvailable();
}

export function setOrchardFrostProperDkgEnabled(on: boolean): void {
	if (typeof localStorage === 'undefined') return;
	localStorage.setItem(ORCHARD_FROST_PROPER_DKG_FLAG_KEY, on ? 'true' : 'false');
}

/**
 * WINBIT32 Orchard file flag for fresh SecureVaults: when **on** the
 * local vault-creation flow exports `.wult` wrapper files containing
 * Orchard FROST trusted-dealer shares alongside the SDK vault data.
 * When **off**, WINBIT32 still prepares/caches the local Orchard share
 * in this browser, but saved files are native Vultisig-compatible
 * `.vult` files with no WINBIT32 private FROST payload.
 *
 * Default is **OFF** because the official Vultisig app's protobuf
 * parser refuses native `.vult` files that carry an unrecognised
 * private Orchard keyshare in `chainKeyShares` (it errors out with
 * `illegal tag: field no 678 wire type 7`). Vultisig's current
 * FROZTO schema work adds public-key fields (`public_key_frozt` /
 * `public_key_fromt`), not a compatible slot for WINBIT32's private
 * Orchard bundle. Keeping this off by default means freshly-created
 * `.vult` files round-trip cleanly through Vultisig; users who only
 * care about WINBIT32 can opt in to `.wult` files for self-contained
 * shielded support.
 *
 * Even with bake-in OFF, transparent Zcash / BTC / EVM signing are
 * unaffected — those chains derive from the standard ECDSA keyshare,
 * which is identical in both modes. Shielded sends just take an
 * locally cached bundle from creation or an extra one-time DKG ceremony
 * over the relay (the OrchardFrost setup wizard) on first use.
 */
export const ORCHARD_BAKE_IN_FLAG_KEY = 'winbit32_orchard_in_secure_vault';

export function isOrchardBakeIntoVaultEnabled(): boolean {
	if (typeof localStorage === 'undefined') return false;
	const raw = localStorage.getItem(ORCHARD_BAKE_IN_FLAG_KEY);
	return raw === 'true';
}

export function setOrchardBakeIntoVaultEnabled(on: boolean): void {
	if (typeof localStorage === 'undefined') return;
	localStorage.setItem(ORCHARD_BAKE_IN_FLAG_KEY, on ? 'true' : 'false');
}

/**
 * Run a single trusted-dealer Orchard FROST keygen for a freshly-created
 * SecureVault — used when every device's DKLS share is generated in one
 * tab so the per-share Orchard bundle can be embedded directly into
 * each `.vult` file (rather than running DKG over the relay later).
 *
 * Each returned bundle carries:
 *   - this device's `myShare` (distinct per index)
 *   - the shared `publicKeyPackage` (identical across devices)
 *   - the shared `orchardExtras` nk‖rivk (so every device derives the
 *     same UFVK / unified address)
 *   - threshold metadata so the bundle survives a round-trip through
 *     `packVultisigOrchardKeyshareBundle`.
 */
export async function generateOrchardBundlesForFreshVault({
	devices,
	minSigners,
	participantPartyIds,
	birthday,
}: {
	devices: number;
	minSigners: number;
	participantPartyIds?: string[];
	birthday?: number;
}): Promise<OrchardKeyBundle[]> {
	if (devices < 2) throw new Error('Orchard FROST keygen needs >= 2 devices');
	const t = Math.max(2, Math.min(minSigners, devices));

	await initOrchardFrostWasm();

	// Orchard's SpendValidatingKey::from_bytes rejects roughly half of all
	// FROST/RedPallas verifying keys (those with the y-sign bit set on
	// byte 31), so retry the whole keygen until we land on an
	// Orchard-compatible group_public. ~1.8 attempts on average.
	let result;
	let extras: string;
	try {
		const combined = orchardKeygenWithCompatibleExtras(devices, t);
		result = combined.keygen;
		extras = combined.orchardExtras;
	} catch (err) {
		console.warn(
			'[orchard-frost] orchardKeygenWithCompatibleExtras failed; trying frozt-wasm fallback:',
			(err as Error).message,
		);
		// Fallback: use whatever keygen produced and try frozt extras.
		result = orchardKeygen(devices, t);
		let froztExtras: string | undefined;
		if (hasFroztOrchardKeys()) {
			try {
				froztExtras = orchardGenerateExtras();
			} catch (err2) {
				console.warn(
					'[orchard-frost] frozt-wasm orchardGenerateExtras also failed:',
					(err2 as Error).message,
				);
			}
		}
		if (!froztExtras) {
			throw new Error(
				'Orchard FROST extras (nk‖rivk) unavailable: orchard-frost-wasm keygen retries exhausted and frozt-wasm fallback unavailable.',
			);
		}
		extras = froztExtras;
	}

	const ids = Object.keys(result.shares);
	if (ids.length !== devices) {
		throw new Error(
			`Expected ${devices} Orchard shares from keygen, got ${ids.length}`,
		);
	}

	const resolvedBirthday = (Number.isFinite(birthday) && (birthday as number) > 0)
		? Math.floor(birthday as number)
		: getSmartOrchardBirthdayHeight();

	// Positional party labels so a caller (e.g. the Orchard FROST setup
	// wizard's local-DKG path) can pick each device's bundle out of the
	// returned array via `bundle.partyName`, mirroring the Monero
	// `MoneroKeyShareBundle` contract. Synthesise stable `share-N` names
	// when the caller doesn't pass aligned party IDs. `partyName` is a
	// transient match key — `packVultisigOrchardKeyshareBundle` never
	// serialises it, so it doesn't survive a `.vult`/`.wult` round-trip.
	const resolvedPartyNames = participantPartyIds && participantPartyIds.length === devices
		? [...participantPartyIds]
		: Array.from({ length: devices }, (_, i) => `share-${i + 1}`);

	return ids.map((id, i): OrchardKeyBundle => ({
		myShare: result.shares[id],
		partyName: resolvedPartyNames[i],
		publicKeyPackage: result.publicKeyPackage,
		orchardExtras: extras,
		minSigners: t,
		maxSigners: devices,
		dkgKind: 'trusted-dealer-threshold',
		// All shares were produced together in this single browser tab as
		// part of the user's vault-creation ceremony. There is no second
		// device whose trust would be improved by an after-the-fact DKG
		// upgrade, so flag the bundle as final and the phrase step UI
		// will stop offering "Upgrade to multi-party DKG".
		bakedAtCreation: true,
		participantPartyIds: participantPartyIds && participantPartyIds.length === devices
			? [...participantPartyIds]
			: undefined,
		birthday: resolvedBirthday,
		ceremonyVersion: CURRENT_ORCHARD_BUNDLE_VERSION,
	}));
}

// -- Storage (in-memory only — see `_orchardBundles` above) -------

function cloneBundle(bundle: OrchardKeyBundle): OrchardKeyBundle {
	// Defensive deep copy via JSON round-trip — the bundle is plain
	// data (strings/numbers/booleans/arrays), no Uint8Array fields.
	return JSON.parse(JSON.stringify(bundle)) as OrchardKeyBundle;
}

export function storeOrchardBundle(vaultUid: string, bundle: OrchardKeyBundle): void {
	if (!vaultUid) return;
	sweepLegacyOrchardLocalStorage();
	_orchardBundles.set(vaultUid, cloneBundle(bundle));
}

export function loadOrchardBundle(vaultUid: string): OrchardKeyBundle | null {
	if (!vaultUid) return null;
	const cached = _orchardBundles.get(vaultUid);
	if (!cached) return null;
	return cloneBundle(cached);
}

export function hasOrchardBundle(vaultUid: string): boolean {
	if (!vaultUid) return false;
	return _orchardBundles.has(vaultUid);
}

export function removeOrchardBundle(vaultUid: string): void {
	if (!vaultUid) return;
	_orchardBundles.delete(vaultUid);
}

/**
 * Drop every in-memory Orchard bundle and decline flag. Intended for
 * "logout" / "switch vault" UI affordances and for test cleanup. The
 * legacy localStorage sweep also runs so any stale data left behind
 * by older builds is purged at the same time.
 */
export function clearAllOrchardBundles(): void {
	_orchardBundles.clear();
	_orchardKeepAsIs.clear();
	sweepLegacyOrchardLocalStorage();
}

// -- Orchard setup-wizard "keep as-is" choice --------------------
//
// When the user explicitly picks "A. Keep things as they are" in
// OrchardFrostSetupWizard we remember that decision per vault so the
// Zcash wizard can stop blocking address derivation and fall through
// to the legacy 2-of-2 bootstrap on first send. Without this flag the
// Zcash wizard would keep re-popping the setup wizard every time the
// vault is loaded.

export function markOrchardFrostKeepAsIs(vaultUid: string): void {
	if (!vaultUid) return;
	_orchardKeepAsIs.add(vaultUid);
}

export function hasOrchardFrostKeepAsIsChoice(vaultUid: string): boolean {
	if (!vaultUid) return false;
	return _orchardKeepAsIs.has(vaultUid);
}

export function clearOrchardFrostKeepAsIs(vaultUid: string): void {
	if (!vaultUid) return;
	_orchardKeepAsIs.delete(vaultUid);
}

/**
 * True when the Orchard FROST setup wizard should open automatically
 * before address derivation proceeds:
 *   - proper-DKG path is enabled (WASM capable)
 *   - no bundle exists, OR a proper-DKG bundle exists but is missing
 *     the shared `orchardExtras` (a legacy bug produced bundles whose
 *     nk‖rivk diverged across devices — re-running the ceremony is the
 *     only way to recover consistent UFVKs everywhere)
 *   - user has not explicitly chosen to keep things as-is
 */
export function shouldPromptOrchardFrostSetup(vaultUid: string): boolean {
	if (!vaultUid) return false;
	if (!isOrchardFrostProperDkgEnabledSafe()) return false;
	if (hasOrchardFrostKeepAsIsChoice(vaultUid)) return false;
	const bundle = loadOrchardBundle(vaultUid);
	if (!bundle) return true;
	// Proper-DKG bundles from before ceremonyVersion=2 generated
	// `orchardExtras` locally and independently on each device, so their
	// nk‖rivk almost certainly diverge across the vault. The wallet can
	// build PCZTs from UFVK_A but the FROST bundle on UFVK_B can't sign
	// them. Re-running the ceremony is the only recovery path.
	if (bundle.dkgKind === 'proper-dkg') {
		if (!bundle.orchardExtras) return true;
		if ((bundle.ceremonyVersion || 1) < CURRENT_ORCHARD_BUNDLE_VERSION) return true;
	}
	return false;
}

/** Narrow helper so we don't crash before the flag helper is defined. */
function isOrchardFrostProperDkgEnabledSafe(): boolean {
	try { return isOrchardFrostProperDkgEnabled(); }
	catch { return false; }
}

// -- DKG (trusted dealer, local) -----------------------------

export interface OrchardDkgResult {
	localBundle: OrchardKeyBundle;
	/** Legacy 2-of-2 field: the single share to hand to the pairing cosigner. */
	remoteShare: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	/** t-of-n field: mapping from Vultisig partyId → that device's share. */
	pendingShares?: Record<string, OrchardKeyShare>;
	minSigners: number;
	maxSigners: number;
}

export interface RunOrchardDkgOptions {
	onProgress?: (msg: string) => void;
	/**
	 * Vultisig localPartyIds of the *other* devices in the vault (excluding
	 * this device). When provided together with the threshold flag, keygen
	 * runs with `maxSigners = otherPartyIds.length + 1` and assigns one
	 * share to each listed party, stored in `bundle.pendingShares`.
	 */
	otherPartyIds?: string[];
	/** Vultisig vault threshold (minSigners). Defaults to 2. */
	minSigners?: number;
}

/**
 * Run Orchard FROST key generation using the trusted-dealer model.
 *
 * Two modes:
 *
 * - **Legacy 2-of-2** (default, backward compatible): generates exactly
 *   two shares. Share 1 is stored locally; share 2 is returned in
 *   `remoteShare` and handed to whichever single co-signer pairs first.
 *
 * - **Threshold t-of-n** (when `isOrchardFrostThresholdEnabled()` and
 *   `otherPartyIds` are supplied): generates `otherPartyIds.length + 1`
 *   shares with threshold `minSigners`. The local share is stored as
 *   `myShare`; every remaining share is mapped to a specific
 *   `otherPartyIds[i]` and stored in `bundle.pendingShares`. When any of
 *   those devices requests a DKG it receives *its* share, not just the
 *   next available one. Any `minSigners` devices can then co-sign Orchard
 *   transactions, matching the Vultisig DKLS threshold model for
 *   transparent transactions.
 */
export async function runOrchardDkg(
	vaultUid: string,
	options?: RunOrchardDkgOptions,
): Promise<OrchardDkgResult> {
	await initOrchardFrostWasm();

	const thresholdEnabled = isOrchardFrostThresholdEnabled();
	const otherPartyIds = (options?.otherPartyIds || []).filter(Boolean);
	const useThreshold = thresholdEnabled && otherPartyIds.length >= 1;

	const maxSigners = useThreshold ? otherPartyIds.length + 1 : 2;
	const minSignersRaw = Number(options?.minSigners);
	// Clamp threshold to [2, maxSigners]. A single-signer vault can't be
	// threshold-signed (we need at least two parties to run FROST).
	const minSigners = useThreshold
		? Math.min(Math.max(Number.isFinite(minSignersRaw) && minSignersRaw >= 2 ? minSignersRaw : 2, 2), maxSigners)
		: 2;

	options?.onProgress?.(
		useThreshold
			? `Generating Orchard FROST key shares (${minSigners}-of-${maxSigners}, threshold mode)...`
			: 'Generating Orchard FROST key shares (2-of-2)...'
	);

	// Orchard's ak rejects ~half of all FROST verifying keys (y-sign bit
	// must be 0). Use the retry helper so we land on a compatible
	// group_public + matching extras in one shot.
	let result;
	let extras: string | undefined;
	try {
		const combined = orchardKeygenWithCompatibleExtras(maxSigners, minSigners);
		result = combined.keygen;
		extras = combined.orchardExtras;
		options?.onProgress?.('Orchard extras generated (orchard-frost-wasm).');
	} catch (err) {
		console.warn('[orchard-dkg] orchard-frost-wasm keygen+extras failed, trying frozt-wasm:', (err as Error).message);
		result = orchardKeygen(maxSigners, minSigners);
		if (hasFroztOrchardKeys()) {
			try {
				extras = orchardGenerateExtras();
				options?.onProgress?.('Orchard extras generated (frozt-wasm).');
			} catch (err2) {
				console.warn('[orchard-dkg] frozt-wasm extras also failed:', (err2 as Error).message);
			}
		}
	}

	const shareIds = Object.keys(result.shares);
	if (shareIds.length !== maxSigners) {
		throw new Error(`Expected ${maxSigners} shares from keygen, got ${shareIds.length}`);
	}

	const localShare = result.shares[shareIds[0]];
	// Legacy field: first-remote share (used by the 2-of-2 hot-path).
	const remoteShare = result.shares[shareIds[1] || shareIds[0]];

	let pendingShares: Record<string, OrchardKeyShare> | undefined;
	if (useThreshold) {
		pendingShares = {};
		for (let i = 0; i < otherPartyIds.length; i++) {
			const partyId = otherPartyIds[i];
			const share = result.shares[shareIds[i + 1]];
			if (!share) {
				throw new Error(
					`Threshold DKG: ran out of shares while assigning to party ${partyId}`
				);
			}
			pendingShares[partyId] = share;
		}
	}

	const localBundle: OrchardKeyBundle = {
		myShare: localShare,
		publicKeyPackage: result.publicKeyPackage,
		orchardExtras: extras,
		pendingShares,
		minSigners,
		maxSigners,
		dkgKind: useThreshold ? 'trusted-dealer-threshold' : 'trusted-dealer-2of2',
		participantPartyIds: useThreshold
			? [/* initiator is implicit — the local device running this */ ...otherPartyIds]
			: undefined,
		birthday: getSmartOrchardBirthdayHeight(),
	};

	storeOrchardBundle(vaultUid, localBundle);

	options?.onProgress?.(
		useThreshold
			? `Orchard FROST ${minSigners}-of-${maxSigners} DKG complete. ${otherPartyIds.length} share(s) queued for delivery.`
			: 'Orchard FROST key shares generated. Local share stored.'
	);

	return {
		localBundle,
		remoteShare,
		publicKeyPackage: result.publicKeyPackage,
		pendingShares,
		minSigners,
		maxSigners,
	};
}

/**
 * Pop the pending Orchard share destined for a specific cosigner party.
 * Returns the share (and removes it from the bundle's pendingShares map)
 * or `null` if no share is queued for this party.
 *
 * Persists the mutation so the same share can't be handed out twice.
 */
export function claimPendingOrchardShare(
	vaultUid: string,
	partyId: string,
): OrchardKeyShare | null {
	const bundle = loadOrchardBundle(vaultUid);
	if (!bundle?.pendingShares || !partyId) return null;
	const share = bundle.pendingShares[partyId];
	if (!share) return null;
	delete bundle.pendingShares[partyId];
	if (Object.keys(bundle.pendingShares).length === 0) {
		delete bundle.pendingShares;
	}
	storeOrchardBundle(vaultUid, bundle);
	return share;
}

// -- Signing (relay-based two-round ceremony) ----------------

interface RelayAdapter {
	send(sessionId: string, from: string, to: string, body: string): Promise<void>;
	receive(sessionId: string, partyName: string): Promise<string[]>;
}

function createRelayAdapter(relayUrl: string): RelayAdapter {
	return {
		async send(sessionId, from, to, body) {
			const resp = await fetch(relayUrl + '/message', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					session_id: sessionId,
					from,
					to: [to],
					body,
				}),
			});
			if (!resp.ok) throw new Error('Relay send failed: ' + resp.status);
		},
		async receive(sessionId, partyName) {
			const resp = await fetch(
				relayUrl + '/message/' + sessionId + '/' + encodeURIComponent(partyName),
			);
			if (!resp.ok) {
				if (resp.status === 404) return [];
				throw new Error('Relay receive failed: ' + resp.status);
			}
			return resp.json();
		},
	};
}

export interface OrchardSignRequest {
	vaultUid: string;
	sighash: string;
	randomizer: string;
	myPartyName: string;
	remotePartyName: string;
	ceremonyOptions: CeremonyOptions;
}

const RELAY_POLL_MS = 500;
const RELAY_TIMEOUT_MS = 120_000;

/**
 * Run a two-round Orchard FROST signing ceremony over the relay.
 *
 * Round 1: Commit nonces, exchange commitments with co-signer.
 * Round 2: Produce signature share, exchange shares, aggregate.
 *
 * Returns the 64-byte RedPallas signature hex.
 */
export async function thresholdOrchardSign(
	request: OrchardSignRequest,
): Promise<string> {
	await initOrchardFrostWasm();

	const bundle = loadOrchardBundle(request.vaultUid);
	if (!bundle) throw new Error('No Orchard key bundle for this vault. Run DKG first.');

	// Default to the WINBIT32 relay (cosign.winbit32.com) — never to
	// api.vultisig.com — when a caller forgot to set `relayUrl`. The
	// cosigner UI / `localStorage.wb32cosign_relay_url` still wins
	// when they're set, matching CosignerApp / orchardDkgCeremony /
	// useZcashSendCore behaviour.
	const explicitRelay = request.ceremonyOptions.relayUrl;
	let relayUrl = (typeof explicitRelay === 'string' && explicitRelay.trim()) ? explicitRelay.trim() : '';
	if (!relayUrl && typeof globalThis !== 'undefined') {
		try {
			const stored = globalThis.localStorage?.getItem?.('wb32cosign_relay_url');
			if (stored && stored.trim()) relayUrl = stored.trim();
		} catch { /* localStorage unavailable */ }
	}
	if (!relayUrl) relayUrl = 'https://cosign.winbit32.com';
	const relay = createRelayAdapter(relayUrl);
	const sessionId = request.ceremonyOptions.sessionId;
	const timeout = request.ceremonyOptions.timeoutMs ?? RELAY_TIMEOUT_MS;
	const startTime = Date.now();

	const myShare = bundle.myShare;
	const pkPkg = bundle.publicKeyPackage;

	// Round 1: commit nonces
	request.ceremonyOptions.onProgress?.('Orchard FROST Round 1: committing nonces...');
	const myCommit = orchardCommitNonces(myShare.secretShare);

	await relay.send(sessionId, request.myPartyName, request.remotePartyName, JSON.stringify({
		round: 1,
		id: myShare.id,
		commitments: myCommit.commitments,
	}));

	// Wait for remote commitments
	let remoteCommitments: string | null = null;
	let remoteId: string | null = null;
	while (!remoteCommitments) {
		if (Date.now() - startTime > timeout) {
			throw new Error('Orchard FROST timed out waiting for co-signer commitments');
		}
		const rawMsgs = await relay.receive(sessionId, request.myPartyName);
		const msgs = Array.isArray(rawMsgs) ? rawMsgs : [];
		for (const raw of msgs) {
			try {
				const msg = JSON.parse(raw);
				if (msg.round === 1 && msg.commitments) {
					remoteCommitments = msg.commitments;
					remoteId = msg.id;
				}
			} catch { /* skip malformed */ }
		}
		if (!remoteCommitments) {
			await new Promise(r => setTimeout(r, RELAY_POLL_MS));
		}
	}

	// Round 2: sign with pre-committed nonces
	request.ceremonyOptions.onProgress?.('Orchard FROST Round 2: signing...');
	const allCommitments: Record<string, string> = {};
	allCommitments[myShare.id] = myCommit.commitments;
	allCommitments[remoteId!] = remoteCommitments;
	const allCommitmentsJson = JSON.stringify(allCommitments);

	const randomizer = request.randomizer || orchardGenerateRandomizer();

	const mySigShare = orchardSignWithNonces(
		myShare.id,
		myShare.secretShare,
		myShare.publicKey,
		pkPkg.groupPublic,
		myCommit.noncesOpaque,
		allCommitmentsJson,
		request.sighash,
		randomizer,
	);

	await relay.send(sessionId, request.myPartyName, request.remotePartyName, JSON.stringify({
		round: 2,
		id: myShare.id,
		sigShare: mySigShare,
	}));

	// Wait for remote sig share
	let remoteSigShare: string | null = null;
	while (!remoteSigShare) {
		if (Date.now() - startTime > timeout) {
			throw new Error('Orchard FROST timed out waiting for co-signer signature share');
		}
		const rawMsgs = await relay.receive(sessionId, request.myPartyName);
		const msgs = Array.isArray(rawMsgs) ? rawMsgs : [];
		for (const raw of msgs) {
			try {
				const msg = JSON.parse(raw);
				if (msg.round === 2 && msg.sigShare) {
					remoteSigShare = msg.sigShare;
				}
			} catch { /* skip malformed */ }
		}
		if (!remoteSigShare) {
			await new Promise(r => setTimeout(r, RELAY_POLL_MS));
		}
	}

	// Aggregate
	request.ceremonyOptions.onProgress?.('Aggregating Orchard signature...');
	const sigShares: Record<string, string> = {};
	sigShares[myShare.id] = mySigShare;
	sigShares[remoteId!] = remoteSigShare;

	const aggregateResultJson = orchardAggregate(
		allCommitmentsJson,
		request.sighash,
		JSON.stringify(sigShares),
		JSON.stringify(pkPkg.signerPubkeys),
		pkPkg.groupPublic,
		randomizer,
	);
	const aggregateResult = JSON.parse(aggregateResultJson);

	request.ceremonyOptions.onProgress?.('Orchard FROST signature complete');
	return aggregateResult.signature as string;
}

// -- Address Derivation from FROST keys ----------------------

/**
 * Derive Orchard address + UFVK from the stored bundle's FROST keys.
 * Returns null if no bundle, no extras, or frozt-wasm unavailable.
 */
export function deriveOrchardFrostAddress(vaultUid: string): {
	unifiedAddress: string; ufvk: string;
} | null {
	const bundle = loadOrchardBundle(vaultUid);
	if (!bundle) return null;
	const hadExtras = !!bundle.orchardExtras;
	const result = deriveOrchardAddressFromBundle(bundle);
	// `deriveOrchardAddressFromBundle` will backfill `bundle.orchardExtras`
	// when it was missing. Persist the upgraded bundle so subsequent loads
	// reuse the same extras (and therefore the same unified address).
	if (result && !hadExtras && bundle.orchardExtras) {
		storeOrchardBundle(vaultUid, bundle);
		console.log(`[orchard-frost] Upgraded bundle for vault ${vaultUid.slice(0, 8)}... now has persistent orchardExtras.`);
	}
	return result;
}

/**
 * Return the raw FROST Orchard material (group_public + 64-byte extras)
 * needed to splice into a seed-derived UFVK so Orchard spends route
 * through FROST while Sapling / transparent stay seed-derived.
 *
 * Backfills and persists `orchardExtras` when missing so subsequent loads
 * produce the same merged unified address.
 */
export function getOrchardFrostMergeMaterial(vaultUid: string): {
	groupPublic: string; extras: string;
} | null {
	const bundle = loadOrchardBundle(vaultUid);
	if (!bundle) return null;
	const groupPublic = bundle.publicKeyPackage?.groupPublic;
	if (!groupPublic) return null;
	if (!bundle.orchardExtras) {
		// Proper-DKG bundles MUST have orchardExtras persisted from the
		// ceremony — backfilling with fresh random bytes would produce
		// a UFVK that differs from every other device's UFVK. Refuse.
		if (bundle.dkgKind === 'proper-dkg') {
			console.error(
				`[orchard-frost] Proper-DKG bundle for vault ${vaultUid.slice(0, 8)}... is missing orchardExtras. Re-run the DKG ceremony; backfilling would diverge from other devices.`,
			);
			return null;
		}
		try {
			bundle.orchardExtras = generateOrchardExtras(groupPublic);
			storeOrchardBundle(vaultUid, bundle);
			console.log(`[orchard-frost] Backfilled orchardExtras for vault ${vaultUid.slice(0, 8)}... and persisted bundle.`);
		} catch (err) {
			console.error('[orchard-frost] Failed to generate orchardExtras:', (err as Error).message);
			return null;
		}
	}
	return { groupPublic, extras: bundle.orchardExtras! };
}

// -- Proper multi-party DKG (no trusted dealer) --------------
//
// Runs the Pedersen 3-round DKG across every vault device via the
// `orchardDkgCeremony` relay module and stores the resulting share
// locally. Each device runs its own instance of this function — one as
// the initiator (shows QR, kicks off ceremony) and N-1 as joiners
// (scan QR, wait for `dkg-start`). The resulting bundle has identical
// `publicKeyPackage.groupPublic` on every device; each holds a distinct
// `myShare`.
//
// Unlike the trusted-dealer flow, NO device ever sees another's secret
// share: every participant only learns its own polynomial evaluation.
// This is the cryptographically clean path and should be the default
// going forward (`ORCHARD_FROST_PROPER_DKG_FLAG_KEY`, default on when
// WASM supports it).

export interface RunInitiatorDkgVaultOptions {
	vaultUid: string;
	session: DkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	callbacks?: DkgCeremonyCallbacks;
	/** Optional pre-built transport for tests. */
	transport?: CosignTransport;
}

export interface RunJoinerDkgVaultOptions {
	vaultUid: string;
	session: DkgSessionParams;
	myPartyId: string;
	initiatorPartyId: string;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

/**
 * Build an `OrchardKeyBundle` from a DKG ceremony result, attach Orchard
 * extras (derived deterministically from `group_public` if the WASM
 * helper is available), and persist to localStorage keyed by vaultUid.
 *
 * Shared between initiator and joiner flows so the stored bundle shape
 * is identical on every device.
 */
async function finaliseDkgBundle(
	vaultUid: string,
	result: DkgCeremonyResult,
): Promise<OrchardKeyBundle> {
	// The ceremony MUST provide the shared orchardExtras. If it doesn't
	// we refuse to persist the bundle — generating fresh random extras
	// locally would silently produce a divergent UFVK per device (same
	// group_public, different nk‖rivk, so every party scans / builds
	// PCZTs against a different FVK) and users would only discover the
	// breakage at spend time, long after the ceremony has concluded.
	if (!result.orchardExtras) {
		throw new Error(
			'DKG ceremony returned no shared orchardExtras. This is a bug: the initiator must broadcast dkg-extras before returning. Refusing to persist a bundle that would diverge across devices.',
		);
	}

	const bundle: OrchardKeyBundle = {
		myShare: result.share,
		publicKeyPackage: result.publicKeyPackage,
		orchardExtras: result.orchardExtras,
		minSigners: result.minSigners,
		maxSigners: result.maxSigners,
		dkgKind: 'proper-dkg',
		participantPartyIds: result.participantPartyIds,
		ceremonyVersion: CURRENT_ORCHARD_BUNDLE_VERSION,
		birthday: getSmartOrchardBirthdayHeight(),
	};
	storeOrchardBundle(vaultUid, bundle);
	return bundle;
}

/**
 * Initiator entry point for the proper DKG ceremony. Expects the caller
 * to display the QR generated from `options.session` so joiners can
 * connect. Returns the local bundle (already persisted) once every
 * device has confirmed a consistent group public key.
 */
export async function runProperOrchardDkgInitiator(
	options: RunInitiatorDkgVaultOptions,
): Promise<OrchardKeyBundle> {
	await initOrchardFrostWasm();
	if (!isProperDkgAvailable()) {
		throw new Error(
			'Proper DKG not supported by the loaded orchard-frost WASM. Update /public/orchard-frost/.',
		);
	}
	const result = await runInitiatorDkgCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		otherPartyIds: options.otherPartyIds,
		minSigners: options.minSigners,
		vaultUid: options.vaultUid,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	return finaliseDkgBundle(options.vaultUid, result);
}

/**
 * Joiner (cosigner) entry point for the proper DKG ceremony. Expects
 * the caller to have scanned a QR and parsed out `session` +
 * `initiatorPartyId`. Returns the local bundle once the ceremony
 * completes successfully.
 */
export async function runProperOrchardDkgJoiner(
	options: RunJoinerDkgVaultOptions,
): Promise<OrchardKeyBundle> {
	await initOrchardFrostWasm();
	if (!isProperDkgAvailable()) {
		throw new Error(
			'Proper DKG not supported by the loaded orchard-frost WASM. Update /public/orchard-frost/.',
		);
	}
	const result = await runJoinerDkgCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		initiatorPartyId: options.initiatorPartyId,
		vaultUid: options.vaultUid,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	return finaliseDkgBundle(options.vaultUid, result);
}

// -- Convenience ---------------------------------------------

export function canDoThresholdOrchard(vaultUid: string): boolean {
	return isOrchardFrostReady() && hasOrchardBundle(vaultUid);
}

/**
 * Return true if the stored bundle was produced by the proper multi-party
 * DKG ceremony. A proper-DKG bundle must NEVER be silently regenerated
 * via the trusted-dealer fallback — doing so would invalidate every other
 * vault device's share. Call this before invoking `runOrchardDkg` on an
 * existing vault.
 */
export function isProperDkgBundle(vaultUid: string): boolean {
	return loadOrchardBundle(vaultUid)?.dkgKind === 'proper-dkg';
}

// ── Trusted-dealer ceremony (Option C — "a little less secure") ────
//
// These wrappers run the same relay-based ceremony as the proper DKG,
// but without the 3-round Pedersen DKG: the initiator generates all
// shares locally and distributes them over the encrypted relay. Use
// this only when all devices can't run the full DKG (e.g. one device
// is missing) but the initiator still needs a working T-of-N Orchard
// FROST share set on every participant.

async function finaliseTrustedDealerBundle(
	vaultUid: string,
	result: TrustedDealerResult,
): Promise<OrchardKeyBundle> {
	let extras = result.orchardExtras;
	if (!extras) {
		try {
			extras = generateOrchardExtras(result.publicKeyPackage.groupPublic);
		} catch (err) {
			console.warn(
				'[orchard-trusted-dealer] orchard-frost-wasm extras failed, trying frozt-wasm:',
				(err as Error).message,
			);
			if (hasFroztOrchardKeys()) {
				try { extras = orchardGenerateExtras(); }
				catch (err2) {
					console.warn('[orchard-trusted-dealer] frozt-wasm extras failed:', (err2 as Error).message);
				}
			}
		}
	}

	const bundle: OrchardKeyBundle = {
		myShare: result.share,
		publicKeyPackage: result.publicKeyPackage,
		orchardExtras: extras,
		minSigners: result.minSigners,
		maxSigners: result.maxSigners,
		dkgKind: 'trusted-dealer-threshold',
		participantPartyIds: result.participantPartyIds,
		birthday: getSmartOrchardBirthdayHeight(),
	};
	storeOrchardBundle(vaultUid, bundle);
	return bundle;
}

export interface RunTrustedDealerInitiatorVaultOptions {
	vaultUid: string;
	session: DkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export interface RunTrustedDealerJoinerVaultOptions {
	vaultUid: string;
	session: DkgSessionParams;
	myPartyId: string;
	initiatorPartyId: string;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export async function runTrustedDealerInitiator(
	options: RunTrustedDealerInitiatorVaultOptions,
): Promise<OrchardKeyBundle> {
	await initOrchardFrostWasm();
	const result = await runInitiatorTrustedDealerCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		otherPartyIds: options.otherPartyIds,
		minSigners: options.minSigners,
		vaultUid: options.vaultUid,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	return finaliseTrustedDealerBundle(options.vaultUid, result);
}

export async function runTrustedDealerJoiner(
	options: RunTrustedDealerJoinerVaultOptions,
): Promise<OrchardKeyBundle> {
	await initOrchardFrostWasm();
	const result = await runJoinerTrustedDealerCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		initiatorPartyId: options.initiatorPartyId,
		vaultUid: options.vaultUid,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	return finaliseTrustedDealerBundle(options.vaultUid, result);
}
